import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import {
  BarLoader,
  Button,
  IconButton,
  Pagination,
  usePagination,
} from '@st-operations/uikit/src/v2'

import Table from '../table'
import Organization from '../organization-config'
import OrganizationHistory from '../organization-history'
import {
  addSourcePartnersToOrg,
  createOrganization,
  getOrganizations,
  setAPADataSources,
  setAPADestinations,
  setAudienceFeatures,
  setAudienceInsights,
  setDisableAudienceBuilder,
  setHidePendingDataInsights,
  setOmmDestinations,
  setOnlineMediaMeasurement,
  setOrgActive,
  setOrgDatasetsLimit,
  setOrgRateLimit,
  setPurchaseBasedData,
  setTaxonomyFeatures,
  setUrlAudience,
  setEnableRevenueReport,
  updateFeatures,
} from '_services/predactiv-admin'

import { isFeatureEnabled } from '../helpers'
import toast from '@st-operations/uikit/lib/toast'

// Correlates every request issued by one save so the audit log can group them into
// a single history entry. crypto.randomUUID needs a secure context, so fall back to
// a good-enough id on plain-http dev hosts.
const buildChangeGroup = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

const OrganizationTab = ({ env }) => {
  const [orgsData, setOrgsData] = useState(null)
  const [editing, setEditing] = useState(false)
  const [viewingHistory, setViewingHistory] = useState(null)

  const getOrgsItems = (response) =>
    response?.map((item) => ({
      data: item,
      values: [
        item.name,
        item.createdBy?.email,
        dayjs(item.createdAt).format('D, MMM YYYY - HH:MM'),
      ],
    }))
  const items = getOrgsItems(orgsData)
  const { page, toPage } = usePagination(items || [], 15)

  useEffect(() => {
    if (!editing) {
      setOrgsData(null)
      getOrganizations(env).then((data) => setOrgsData(data.items))
    }
  }, [env, editing])

  // One request per section, straight to the existing per-field endpoints — no
  // aggregate endpoint in between. A section is only sent when the form supplied a
  // value for it, so an untouched field never fails its endpoint's presence check.
  const buildConfigTasks = ({ id, changeGroup, config, allFeatures, features }) => {
    const {
      active,
      audienceFeatures,
      audienceInsights,
      purchaseBasedData,
      disableAudienceBuilder,
      hidePendingDataInsights,
      onlineMediaMeasurement,
      ommDestinations,
      urlAudience,
      enableRevenueReport,
      taxonomy_features,
      datasetLimit,
      rateLimits,
      selectedSourcePartners,
      dataSources,
      destinations,
    } = config

    const base = { env, id, changeGroup }
    const tasks = []
    const add = (field, value, run) => {
      if (value === undefined || value === null) return
      tasks.push([field, run])
    }

    add('features', features, () => updateFeatures({ ...base, features }))
    add('audience_features', audienceFeatures, () =>
      setAudienceFeatures({ ...base, audienceFeatures })
    )
    add('audience_insights', audienceInsights, () =>
      setAudienceInsights({ ...base, audienceInsights })
    )
    add('purchase_based_data', purchaseBasedData, () =>
      setPurchaseBasedData({ ...base, purchaseBasedData })
    )
    add('disable_audience_builder', disableAudienceBuilder, () =>
      setDisableAudienceBuilder({ ...base, disableAudienceBuilder })
    )
    add('hide_pending_data_insights', hidePendingDataInsights, () =>
      setHidePendingDataInsights({ ...base, hidePendingDataInsights })
    )
    add('online_media_measurement', onlineMediaMeasurement, () =>
      setOnlineMediaMeasurement({ ...base, onlineMediaMeasurement })
    )
    // Sent even when empty: [] is a real value here, it clears the picks.
    add('omm_destinations', ommDestinations, () => setOmmDestinations({ ...base, ommDestinations }))
    add('url_audience', urlAudience, () => setUrlAudience({ ...base, urlAudience }))
    add('enable_revenue_report', enableRevenueReport, () =>
      setEnableRevenueReport({ ...base, enableRevenueReport })
    )
    add('taxonomy_features', taxonomy_features, () =>
      setTaxonomyFeatures({ ...base, taxonomy_features })
    )
    add('dataset_limit', datasetLimit, () => setOrgDatasetsLimit({ ...base, datasetLimit }))
    add('rate_limits', rateLimits, () => {
      const entries = Object.entries(rateLimits || {})
      if (!entries.length) return Promise.resolve()
      return Promise.all(
        entries.map(([apiType, limit]) => setOrgRateLimit({ ...base, limit, apiType }))
      )
    })
    add('source_partners', selectedSourcePartners, () =>
      addSourcePartnersToOrg(env, id, selectedSourcePartners, changeGroup)
    )
    add('active', active, () => setOrgActive({ ...base, active }))

    // Only send data sources / destinations when their feature is enabled (matches prior behavior).
    if (isFeatureEnabled('data_source_config', allFeatures) && dataSources?.length) {
      add('data_sources', dataSources, () =>
        setAPADataSources({ ...base, data_sources: dataSources })
      )
    }
    if (isFeatureEnabled('audience_delivery_destination', allFeatures) && destinations?.length) {
      add('destinations', destinations, () => setAPADestinations({ ...base, destinations }))
    }

    return tasks
  }

  const handleSaveOrg = async (config) => {
    const { id, permissions, orgName } = config

    // Flatten the toggle sections into the { _id, toggle } shape the feature-toggle endpoint expects.
    const allFeatures = Object.values(permissions || {}).flat()
    const features = allFeatures.map(({ _id, toggle }) => ({ _id, toggle }))
    const changeGroup = buildChangeGroup()

    try {
      toast('Saving organization...')

      // A new organization has to exist before its config can be applied.
      let orgId = id
      if (!orgId) {
        const created = await createOrganization({ env, name: orgName, changeGroup })
        orgId = created?._id
        if (!orgId) throw new Error('Organization was created without an id')
      }

      const tasks = buildConfigTasks({ id: orgId, changeGroup, config, allFeatures, features })
      const results = await Promise.allSettled(tasks.map(([, run]) => run()))
      const failed = tasks
        .filter((_task, index) => results[index].status === 'rejected')
        .map(([field]) => field)

      if (failed.length) {
        toast.error(`Saved with errors: ${failed.join(', ')}`)
      } else {
        toast.success(id ? 'Organization saved!' : 'Organization created!')
      }
    } catch (error) {
      toast.error(error.message || 'Error saving organization')
    } finally {
      setEditing(false)
    }
  }

  const renderAction = (data) => (
    <>
      <IconButton
        onClick={() => setEditing(data)}
        aria-label="Edit Organization"
        iconProps={{
          icon: 'edit',
          type: 'outline',
          width: 16,
          height: 16,
          color: '#3D3C68',
        }}
      />
      <IconButton
        onClick={() => setViewingHistory(data)}
        aria-label="View Organization History"
        iconProps={{
          icon: 'clock',
          type: 'outline',
          width: 16,
          height: 16,
          color: '#3D3C68',
        }}
      />
      {/* <IconButton
        onClick={() => {}}
        aria-label="Delete Organization"
        iconProps={{
          icon: 'trashcan',
          type: 'outline',
          width: 16,
          height: 16,
          color: '#D92323',
        }}
      /> */}
    </>
  )

  if (!orgsData) return <BarLoader />

  if (editing) {
    return (
      <Organization
        data={editing}
        onCancel={() => setEditing(false)}
        env={env}
        onSave={handleSaveOrg}
      />
    )
  }

  if (viewingHistory) {
    return (
      <OrganizationHistory
        organizationId={viewingHistory._id}
        organizationName={viewingHistory.name}
        env={env}
        onCancel={() => setViewingHistory(null)}
      />
    )
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Button onClick={() => setEditing({})}>Create New Organization</Button>
        <Pagination
          options={{ length: page.quantity }}
          page={page.number - 1}
          onPageChange={(page) => toPage(page.label)}
        />
      </div>
      <Table
        items={page.content}
        header={['Organization', 'Created By', 'Date Created']}
        renderAction={renderAction}
        gridTemplateColumns={'1.5fr repeat(2, 1fr) min-content'}
        highlightOnHover
      />
    </>
  )
}

export default OrganizationTab
